import { View, Text, Image } from '@react-pdf/renderer';

import { AttractorField, VarusValgusField } from '@/components/PDFDownload/Components/PDFFields';
import { PDFFormatting, PDFTextStyles } from '@/components/PDFDownload/styles';

import HipExtenstionAtTerminalStance from '@/assets/avatars/walk/analysis/HipExtensionAtTerminalStance.png';
import HipExtensionAtTerminalSwing from '@/assets/avatars/walk/analysis/HipExtensionAtTerminalSwing.png';
import KneeExtensionAtTerminalStance from '@/assets/avatars/walk/analysis/KneeExtensionAtTerminalStance.png';
import KneeFlexionAtMidstance from '@/assets/avatars/walk/analysis/KneeFlexionAtMidstance.png';
import KneeFlexionAtMidswing from '@/assets/avatars/walk/analysis/KneeFlexionAtMidswing.png';
import KneeStabilityAtLoadingResponse from '@/assets/avatars/walk/analysis/KneeStabilityAtLoadingResponse.png';
import PelvicObliquity from '@/assets/avatars/walk/analysis/PelvicObliquity.png';
import VarusValgus from '@/assets/avatars/walk/analysis/VarusValgus.png';

type AttractorLabels = string[][];

type AttractorCardProps = {
  title: string;
  subtitle?: string;
  img: string;
  attractorRow: number[];
  kpiLabels: string[];
  varusValgus?: string[];
};

/** Report card for kpi data
 * @param img - The avatar
 * @param kpiRow - Tuple of (Left, Right) kpi values
 * @param labels - rows of kpi labels, row 0 is Left, row 1 is Right
 */
const AttractorCard = ({ title, subtitle, img, attractorRow, kpiLabels, varusValgus }: AttractorCardProps) => {
  const kpiLabel = (labels: string[], kpi: number) => {
    return labels[kpi - 1];
  };

  return (
    <View debug={false} style={PDFFormatting.attractorCardContainer}>
      <Text style={PDFTextStyles.title}>{title}</Text>
      <Text style={PDFTextStyles.subtitle}>{subtitle}</Text>
      <Image src={img} style={PDFFormatting.attractorCardAvatar} />
      <View style={PDFFormatting.cardResultContainer}>
        {kpiLabels && !varusValgus && (
          <>
            <AttractorField name={'Left'} value={kpiLabel(kpiLabels, Math.round(attractorRow[0]))} />
            <AttractorField name={'Right'} value={kpiLabel(kpiLabels, Math.round(attractorRow[1]))} />
          </>
        )}
        {varusValgus && kpiLabels && (
          <>
            <VarusValgusField
              label={varusValgus[0]}
              name={'Left'}
              value={kpiLabel(kpiLabels, Math.round(attractorRow[0]))}
            />
            <VarusValgusField
              label={varusValgus[1]}
              name={'Right'}
              value={kpiLabel(kpiLabels, Math.round(attractorRow[1]))}
            />
          </>
        )}
      </View>
    </View>
  );
};

type WalkAnalysisCardProps = {
  kpiLabels: string[][];
  kpiData: number[][];
  romData: number[][];
};

export const WalkAnalysisCards = ({ kpiLabels, kpiData, romData }: WalkAnalysisCardProps) => {
  const varusValgusLabel_Left = romData[6][0] < 0 ? 'Valgus' : 'Varus';
  const varusValgusLabel_Right = romData[6][1] < 0 ? 'Valgus' : 'Varus';
  return (
    <>
      <View style={PDFFormatting.KpiCardscontainer}>
        <AttractorCard
          attractorRow={kpiData[1]}
          img={HipExtenstionAtTerminalStance}
          kpiLabels={kpiLabels[0]}
          subtitle={'Terminal stance'}
          title={'Hip extension'}
        />
        <AttractorCard
          attractorRow={kpiData[0]}
          img={KneeExtensionAtTerminalStance}
          kpiLabels={kpiLabels[2]}
          subtitle={'Terminal stance'}
          title={'Knee extension'}
        />
        <AttractorCard
          attractorRow={kpiData[6]}
          img={KneeFlexionAtMidswing}
          kpiLabels={kpiLabels[0]}
          subtitle={'Midswing'}
          title={'Knee flexion'}
        />
        <AttractorCard
          attractorRow={kpiData[2]}
          img={HipExtensionAtTerminalSwing}
          kpiLabels={kpiLabels[0]}
          subtitle={'Terminal swing'}
          title={'Hip extension'}
        />
      </View>
      <View style={PDFFormatting.KpiCardscontainer}>
        <AttractorCard
          attractorRow={kpiData[3]}
          img={KneeStabilityAtLoadingResponse}
          kpiLabels={kpiLabels[0]}
          subtitle={'Loading response'}
          title={'Knee stability'}
        />
        <AttractorCard
          attractorRow={kpiData[5]}
          img={KneeFlexionAtMidstance}
          kpiLabels={kpiLabels[0]}
          subtitle={'Midstance'}
          title={'Knee flexion'}
        />
        <AttractorCard
          attractorRow={kpiData[7]}
          img={VarusValgus}
          kpiLabels={kpiLabels[0]}
          subtitle={'Midstance'}
          title={'Varus / Valgus'}
          varusValgus={[varusValgusLabel_Left, varusValgusLabel_Right]}
        />
        <AttractorCard
          attractorRow={kpiData[4]}
          img={PelvicObliquity}
          kpiLabels={kpiLabels[1]}
          subtitle={'Midstance'}
          title={'Pelvic obliquity'}
        />
      </View>
    </>
  );
};
